import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  useTheme,
  CircularProgress,
  Alert,
  Avatar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Send as SendIcon, SmartToy as BotIcon, Person as PersonIcon } from '@mui/icons-material';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';
import { motion, AnimatePresence } from 'framer-motion';

const ChatContainer = styled(Paper)<{ isRTL?: boolean }>(({ theme, isRTL }) => ({
  height: 'calc(100vh - 200px)',
  minHeight: '600px',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(2),
  boxShadow: `0 4px 20px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  direction: isRTL ? 'rtl' : 'ltr',
}));

const MessagesContainer = styled(Box)<{ isRTL?: boolean }>(({ theme, isRTL }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f5f5f5',
  direction: isRTL ? 'rtl' : 'ltr',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.mode === 'dark' ? '#2d2d2d' : '#f1f1f1',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.primary.main,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.primary.dark,
  },
}));

const MessageBubble = styled(Paper)<{ isUser?: boolean; isRTL?: boolean }>(({ theme, isUser, isRTL }) => ({
  padding: theme.spacing(1.5, 2),
  maxWidth: '70%',
  alignSelf: isUser ? (isRTL ? 'flex-start' : 'flex-end') : (isRTL ? 'flex-end' : 'flex-start'),
  backgroundColor: isUser 
    ? theme.palette.primary.main 
    : theme.palette.mode === 'dark' 
      ? theme.palette.grey[800] 
      : theme.palette.background.paper,
  color: isUser ? theme.palette.primary.contrastText : theme.palette.text.primary,
  borderRadius: theme.spacing(2),
  boxShadow: `0 2px 8px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
  display: 'flex',
  gap: theme.spacing(1.5),
  alignItems: 'flex-start',
  flexDirection: isRTL ? 'row-reverse' : 'row',
}));

const InputContainer = styled(Box)<{ isRTL?: boolean }>(({ theme, isRTL }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'flex-end',
  flexDirection: isRTL ? 'row-reverse' : 'row',
  direction: isRTL ? 'rtl' : 'ltr',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#f5f5f5',
  },
}));

const SendButton = styled(Button)(({ theme }) => ({
  minWidth: '56px',
  height: '56px',
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'scale(1.05)',
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
  },
  transition: 'all 0.2s ease-in-out',
}));

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

const STORAGE_KEY = 'cricut_chat_history';

// Function to detect if text contains Hebrew characters
const containsHebrew = (text: string): boolean => {
  const hebrewPattern = /[\u0590-\u05FF]/;
  return hebrewPattern.test(text);
};

// Function to get proper text direction for mixed content
const getTextDirection = (text: string): 'rtl' | 'ltr' => {
  if (containsHebrew(text)) {
    return 'rtl';
  }
  return 'ltr';
};

export const ChatPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { currentLang } = useLanguage();
  const isRTL = currentLang === 'he';
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Load messages from sessionStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsedMessages = JSON.parse(stored);
        setMessages(parsedMessages);
      }
    } catch (err) {
      console.error('Failed to load chat history:', err);
    }
  }, []);

  // Save messages to sessionStorage whenever they change
  useEffect(() => {
    try {
      if (messages.length > 0) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (err) {
      console.error('Failed to save chat history:', err);
    }
  }, [messages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Clear history when page visibility changes (user switched tabs)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Tab is visible again, but we keep the session
        // Session will only clear when tab is closed (which clears sessionStorage)
      }
    };

    // Clear session when tab is closed (using beforeunload as proxy)
    const handleBeforeUnload = () => {
      sessionStorage.removeItem(STORAGE_KEY);
    };

    // Note: We can't detect tab close directly, but sessionStorage clears automatically
    // when the tab is closed. For tab switching, we'll keep the session.
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleSend = async () => {
    if (!inputValue.trim() || loading) return;

    const userMessage: Message = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: Date.now(),
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInputValue('');
    setError(null);
    setLoading(true);

    try {
      // Prepare messages for API (exclude timestamp and system messages)
      const apiMessages = newMessages.map(({ role, content }) => ({
        role,
        content,
      }));

      const response = await api.post('/chat/message', {
        messages: apiMessages,
      });

      const assistantMessage: Message = {
        role: 'assistant',
        content: response.data.data.message,
        timestamp: Date.now(),
      };

      setMessages([...newMessages, assistantMessage]);
    } catch (err: any) {
      console.error('Error sending message:', err);
      setError(err.response?.data?.error || t('pages.chat.error'));
      
      // Remove the user message on error
      setMessages(messages);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearChat = () => {
    setMessages([]);
    sessionStorage.removeItem(STORAGE_KEY);
  };

  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        py: 4,
        direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box 
          sx={{ 
            mb: 3, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            direction: isRTL ? 'rtl' : 'ltr',
            flexDirection: isRTL ? 'row-reverse' : 'row',
          }}
        >
          {messages.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearChat}
              sx={{ minWidth: '100px' }}
            >
              {t('pages.chat.clear')}
            </Button>
          )}
          <Typography
            variant="h3"
            component="div"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || theme.palette.primary.dark})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textAlign: isRTL ? 'right' : 'left',
              direction: isRTL ? 'rtl' : 'ltr',
              unicodeBidi: 'plaintext',
            }}
          >
            {t('pages.chat.title')}
          </Typography>
        </Box>

        <Typography
          variant="body1"
          component="div"
          sx={{
            mb: 3,
            color: 'text.secondary',
            textAlign: isRTL ? 'right' : 'left',
            direction: isRTL ? 'rtl' : 'ltr',
            unicodeBidi: 'plaintext',
          }}
        >
          {t('pages.chat.description')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <ChatContainer isRTL={isRTL}>
          <MessagesContainer ref={messagesContainerRef} isRTL={isRTL}>
            <AnimatePresence>
              {messages.length === 0 ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    color: 'text.secondary',
                    gap: 2,
                    direction: isRTL ? 'rtl' : 'ltr',
                  }}
                >
                  <BotIcon sx={{ fontSize: 64, color: 'primary.main', opacity: 0.5 }} />
                  <Typography 
                    variant="h6" 
                    component="div"
                    sx={{ 
                      direction: isRTL ? 'rtl' : 'ltr',
                      unicodeBidi: 'plaintext',
                      textAlign: 'center',
                    }}
                  >
                    {t('pages.chat.welcome')}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    component="div"
                    sx={{ 
                      direction: isRTL ? 'rtl' : 'ltr',
                      unicodeBidi: 'plaintext',
                      textAlign: 'center',
                    }}
                  >
                    {t('pages.chat.welcomeSubtitle')}
                  </Typography>
                </Box>
              ) : (
                messages.map((message, index) => {
                  const textDirection = getTextDirection(message.content);
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <MessageBubble isUser={message.role === 'user'} isRTL={isRTL}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: message.role === 'user' 
                              ? 'rgba(255, 255, 255, 0.2)' 
                              : 'primary.main',
                          }}
                        >
                          {message.role === 'user' ? (
                            <PersonIcon fontSize="small" />
                          ) : (
                            <BotIcon fontSize="small" />
                          )}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body1"
                            component="div"
                            sx={{
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              direction: textDirection,
                              textAlign: textDirection === 'rtl' ? 'right' : 'left',
                              unicodeBidi: 'plaintext',
                            }}
                          >
                            {message.content}
                          </Typography>
                        </Box>
                      </MessageBubble>
                    </motion.div>
                  );
                })
              )}
            </AnimatePresence>

            {loading && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  alignSelf: isRTL ? 'flex-end' : 'flex-start',
                  flexDirection: isRTL ? 'row-reverse' : 'row',
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: 'primary.main',
                  }}
                >
                  <BotIcon fontSize="small" />
                </Avatar>
                <Paper
                  sx={{
                    padding: theme.spacing(1.5, 2),
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? theme.palette.grey[800] 
                      : theme.palette.background.paper,
                  }}
                >
                  <CircularProgress size={20} />
                </Paper>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </MessagesContainer>

          <InputContainer isRTL={isRTL}>
            <StyledTextField
              multiline
              maxRows={4}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('pages.chat.placeholder')}
              disabled={loading}
              variant="outlined"
              fullWidth
              autoFocus
              sx={{
                direction: isRTL ? 'rtl' : 'ltr',
                '& .MuiOutlinedInput-input': {
                  direction: isRTL ? 'rtl' : 'ltr',
                  textAlign: isRTL ? 'right' : 'left',
                },
              }}
            />
            <SendButton
              variant="contained"
              onClick={handleSend}
              disabled={!inputValue.trim() || loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <SendIcon />
              )}
            </SendButton>
          </InputContainer>
        </ChatContainer>
      </motion.div>
    </Container>
  );
};

